import React from 'react';
import {
  Box,
  Divider,
  Stack,
  Typography,
  Link as MuiLink,
} from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { formatCurrency } from 'renderer/shared/formatters/formatters';
import CopyToClipboardButton from 'renderer/shared/components/CopyToClipboardButton';
import { TransactionDetails } from '../schemas/transaction.schemas';
import {
  getTransactionPaymentMethodExpiration,
  getTransactionPaymentMethodLabel,
  getTransactionProcessor,
} from '../schemas/transaction.transformer';

import TransactionProcessorBadge from './TransactionProcessorBadge';

type TransactionPaymentMethodInfoProps = {
  transaction: TransactionDetails;
  showProviderDashboardLink?: boolean;
};

const CopyableProcessorId = ({
  label,
  value,
}: {
  label: string;
  value: string;
}) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'flex-start',
      gap: 0.5,
    }}
  >
    <Typography
      variant="body2"
      color="text.secondary"
      sx={{ flexShrink: 0, minWidth: 148, pt: '7px' }}
    >
      {label}
    </Typography>
    <Typography
      variant="body2"
      component="span"
      sx={{
        fontFamily: 'monospace',
        wordBreak: 'break-all',
        flex: 1,
        pt: '7px',
      }}
    >
      {value}
    </Typography>
    <CopyToClipboardButton textToCopy={value} />
  </Box>
);

const DetailRow = ({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) => (
  <Box sx={{ display: 'flex', gap: 1, alignItems: 'baseline' }}>
    <Typography
      variant="body2"
      color="text.secondary"
      sx={{ minWidth: 88, flexShrink: 0 }}
    >
      {label}
    </Typography>
    <Typography variant="body2" component="div" sx={{ flex: 1 }}>
      {children}
    </Typography>
  </Box>
);

const TransactionPaymentMethodInfo = ({
  transaction,
  showProviderDashboardLink = false,
}: TransactionPaymentMethodInfoProps) => {
  const paymentMethodLabel = getTransactionPaymentMethodLabel(transaction);
  const processor = getTransactionProcessor(transaction);
  const expiration = getTransactionPaymentMethodExpiration(transaction);
  const billingAddress = transaction.embeddedPaymentMethod?.billingAddress;
  const hasProcessorId = Boolean(
    transaction.paypalId ||
      transaction.braintreeTransactionId ||
      transaction.stripeId,
  );

  if (!paymentMethodLabel && !processor && !hasProcessorId) {
    return null;
  }

  const billingLine = [
    billingAddress?.streetAddress,
    billingAddress?.locality,
    billingAddress?.region,
    billingAddress?.postalCode,
    billingAddress?.countryName,
  ]
    .filter(Boolean)
    .join(', ');

  return (
    <Box>
      <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
        Payment
      </Typography>
      <Stack spacing={1.5}>
        {processor && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <TransactionProcessorBadge processor={processor} />
            {paymentMethodLabel && (
              <Typography variant="body2">
                {paymentMethodLabel} · {formatCurrency(transaction.amount)}
              </Typography>
            )}
          </Box>
        )}

        {!processor && paymentMethodLabel && (
          <DetailRow label="Method">
            {paymentMethodLabel} · {formatCurrency(transaction.amount)}
          </DetailRow>
        )}

        {expiration && <DetailRow label="Expires">{expiration}</DetailRow>}

        {hasProcessorId && (
          <Stack spacing={0.75}>
            {transaction.paypalId && (
              <CopyableProcessorId
                label="PayPal capture ID"
                value={transaction.paypalId}
              />
            )}
            {transaction.braintreeTransactionId && (
              <CopyableProcessorId
                label="Braintree ID"
                value={transaction.braintreeTransactionId}
              />
            )}
            {transaction.stripeId && (
              <CopyableProcessorId
                label="Stripe payment ID"
                value={transaction.stripeId}
              />
            )}
          </Stack>
        )}

        {showProviderDashboardLink &&
          transaction.providerDashboardUrl &&
          transaction.paymentProvider && (
            <MuiLink
              href={transaction.providerDashboardUrl}
              target="_blank"
              rel="noopener noreferrer"
              underline="hover"
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.5,
                width: 'fit-content',
              }}
            >
              View in {transaction.paymentProvider}
              <OpenInNewIcon sx={{ fontSize: 16 }} />
            </MuiLink>
          )}

        {(billingAddress?.nameOnCard || billingLine) && (
          <>
            <Divider />
            <Stack spacing={0.75}>
              {billingAddress?.nameOnCard && (
                <DetailRow label="Name on card">
                  {billingAddress.nameOnCard}
                </DetailRow>
              )}
              {billingLine && (
                <DetailRow label="Billing">{billingLine}</DetailRow>
              )}
              <DetailRow label="Delivery">Electronic Delivery</DetailRow>
            </Stack>
          </>
        )}
      </Stack>
    </Box>
  );
};

export default TransactionPaymentMethodInfo;
